import { useState } from 'react';
import { t, tp } from '../lib/i18n';
import type { RewardInput } from '../lib/types';

interface Props {
  rewards: RewardInput[];
  onChange: (rewards: RewardInput[]) => void;
  campaignLuckyDrawRatio: number;
}

const TRIGGER_TYPE_VALUES: RewardInput['triggerType'][] = [
  'lucky_draw',
  'referral_milestone',
  'top_n_daily',
  'top_n_weekly',
  'top_n_monthly',
  'top_n_overall',
];

const REWARD_TYPE_VALUES: RewardInput['rewardType'][] = [
  'voucher_code',
  'platform_credits',
  'custom',
];

function summary(r: RewardInput, campaignLuckyDrawRatio: number): string {
  switch (r.triggerType) {
    case 'lucky_draw': {
      const ratio = r.triggerLuckyDrawRatio ?? campaignLuckyDrawRatio;
      return tp('rewardSummary.luckyDraw', { n: ratio });
    }
    case 'referral_milestone':
      return tp('rewardSummary.referrals', { n: r.triggerValue ?? '?' });
    case 'top_n_daily':
    case 'top_n_weekly':
    case 'top_n_monthly':
    case 'top_n_overall':
      return tp('rewardSummary.topN', { n: r.triggerValue ?? '?' });
  }
}

export default function RewardConfigurator({ rewards, onChange, campaignLuckyDrawRatio }: Props) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  function addReward() {
    const next: RewardInput = {
      triggerType: 'referral_milestone',
      triggerValue: 3,
      rewardType: 'custom',
      rewardLabel: '',
      rewardValueEur: 0,
      rewardRealCostEur: 0,
      rewardQuantity: 1,
      isCumulative: true,
      position: rewards.length,
    };
    onChange([...rewards, next]);
    setEditingIndex(rewards.length);
  }

  function update(index: number, patch: Partial<RewardInput>) {
    onChange(rewards.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  function remove(index: number) {
    onChange(rewards.filter((_, i) => i !== index));
    if (editingIndex === index) setEditingIndex(null);
    else if (editingIndex !== null && editingIndex > index) {
      setEditingIndex(editingIndex - 1);
    }
  }

  function move(index: number, dir: -1 | 1) {
    const target = index + dir;
    if (target < 0 || target >= rewards.length) return;
    const next = rewards.slice();
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next.map((r, i) => ({ ...r, position: i })));
    if (editingIndex === index) setEditingIndex(target);
    else if (editingIndex === target) setEditingIndex(index);
  }

  return (
    <div>
      <div className="flex-between" style={{ marginBottom: '0.75rem' }}>
        <h3 style={{ margin: 0 }}>{t('rewards.title')}</h3>
        <button onClick={addReward}>{t('rewards.add')}</button>
      </div>

      {rewards.length === 0 && <div className="notice">{t('rewards.empty')}</div>}

      {rewards.map((r, idx) =>
        editingIndex === idx ? (
          <RewardEditor
            key={idx}
            index={idx}
            reward={r}
            campaignLuckyDrawRatio={campaignLuckyDrawRatio}
            onChange={(patch) => update(idx, patch)}
            onClose={() => setEditingIndex(null)}
            onDelete={() => remove(idx)}
          />
        ) : (
          <RewardRow
            key={idx}
            index={idx}
            reward={r}
            isFirst={idx === 0}
            isLast={idx === rewards.length - 1}
            onEdit={() => setEditingIndex(idx)}
            onMoveUp={() => move(idx, -1)}
            onMoveDown={() => move(idx, 1)}
            onDelete={() => remove(idx)}
            campaignLuckyDrawRatio={campaignLuckyDrawRatio}
          />
        ),
      )}
    </div>
  );
}

interface RowProps {
  index: number;
  reward: RewardInput;
  isFirst: boolean;
  isLast: boolean;
  onEdit: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  campaignLuckyDrawRatio: number;
}

function RewardRow({
  index,
  reward,
  isFirst,
  isLast,
  onEdit,
  onMoveUp,
  onMoveDown,
  onDelete,
  campaignLuckyDrawRatio,
}: RowProps) {
  const label = reward.rewardLabel || (
    <span className="muted">{t('rewards.noLabel')}</span>
  );
  const value = reward.rewardValueEur > 0 ? `€${reward.rewardValueEur.toFixed(2)}` : '€0';
  const max = reward.maxWinners != null ? ` · max ${reward.maxWinners}` : '';

  return (
    <div
      className="card"
      style={{
        marginBottom: '0.5rem',
        padding: '0.6rem 0.85rem',
        cursor: 'pointer',
      }}
      onClick={onEdit}
      role="button"
    >
      <div className="flex-between" style={{ gap: '0.75rem' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="row" style={{ gap: '0.5rem', alignItems: 'center' }}>
            <span className="muted small" style={{ minWidth: '1.5rem' }}>#{index + 1}</span>
            <span className="badge">{t(`triggerType.${reward.triggerType}`)}</span>
            <span className="small muted">{summary(reward, campaignLuckyDrawRatio)}</span>
          </div>
          <div style={{ marginTop: '0.25rem', fontWeight: 600 }}>{label}</div>
          <div className="small muted">
            {value}{max}
          </div>
        </div>
        <div className="row" onClick={(e) => e.stopPropagation()}>
          <button
            className="secondary"
            onClick={onMoveUp}
            disabled={isFirst}
            title={t('rewards.moveUp')}
          >
            ↑
          </button>
          <button
            className="secondary"
            onClick={onMoveDown}
            disabled={isLast}
            title={t('rewards.moveDown')}
          >
            ↓
          </button>
          <button onClick={onEdit}>{t('common.edit')}</button>
          <button className="danger" onClick={onDelete} title={t('common.delete')}>×</button>
        </div>
      </div>
    </div>
  );
}

interface EditorProps {
  index: number;
  reward: RewardInput;
  campaignLuckyDrawRatio: number;
  onChange: (patch: Partial<RewardInput>) => void;
  onClose: () => void;
  onDelete: () => void;
}

function RewardEditor({
  index,
  reward,
  campaignLuckyDrawRatio,
  onChange,
  onClose,
  onDelete,
}: EditorProps) {
  return (
    <div className="card" style={{ marginBottom: '0.5rem', borderColor: 'var(--accent)' }}>
      <div className="flex-between" style={{ marginBottom: '0.75rem' }}>
        <strong>{tp('rewards.editTitle', { n: index + 1 })}</strong>
        <div className="row">
          <button className="danger" onClick={onDelete}>{t('common.delete')}</button>
          <button onClick={onClose}>{t('common.done')}</button>
        </div>
      </div>

      <div className="grid-2">
        <div>
          <label>{t('rewardField.triggerType')}</label>
          <select
            value={reward.triggerType}
            onChange={(e) =>
              onChange({ triggerType: e.target.value as RewardInput['triggerType'] })
            }
          >
            {TRIGGER_TYPE_VALUES.map((v) => (
              <option key={v} value={v}>{t(`triggerType.${v}`)}</option>
            ))}
          </select>
        </div>

        {reward.triggerType === 'referral_milestone' && (
          <div>
            <label>{t('rewardField.referralCount')}</label>
            <input
              type="number"
              min={1}
              value={reward.triggerValue ?? ''}
              onChange={(e) => onChange({ triggerValue: Number(e.target.value) })}
            />
          </div>
        )}

        {reward.triggerType.startsWith('top_n_') && (
          <>
            <div>
              <label>{t('rewardField.topN')}</label>
              <input
                type="number"
                min={1}
                value={reward.triggerValue ?? ''}
                onChange={(e) => onChange({ triggerValue: Number(e.target.value) })}
              />
            </div>
            {reward.triggerType !== 'top_n_overall' && (
              <div>
                <label>{t('rewardField.cumulative')}</label>
                <select
                  value={reward.isCumulative ? '1' : '0'}
                  onChange={(e) => onChange({ isCumulative: e.target.value === '1' })}
                >
                  <option value="1">{t('rewardField.cumulativeYes')}</option>
                  <option value="0">{t('rewardField.cumulativeNo')}</option>
                </select>
              </div>
            )}
          </>
        )}

        {reward.triggerType === 'lucky_draw' && (
          <div>
            <label>
              {tp('rewardField.luckyDrawRatio', { ratio: campaignLuckyDrawRatio })}
            </label>
            <input
              type="number"
              min={1}
              value={reward.triggerLuckyDrawRatio ?? ''}
              onChange={(e) =>
                onChange({
                  triggerLuckyDrawRatio: e.target.value ? Number(e.target.value) : null,
                })
              }
            />
          </div>
        )}

        <div>
          <label>{t('rewardField.rewardType')}</label>
          <select
            value={reward.rewardType}
            onChange={(e) => onChange({ rewardType: e.target.value as RewardInput['rewardType'] })}
          >
            {REWARD_TYPE_VALUES.map((v) => (
              <option key={v} value={v}>{t(`rewardType.${v}`)}</option>
            ))}
          </select>
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <label>{t('rewardField.label')}</label>
          <input
            value={reward.rewardLabel}
            onChange={(e) => onChange({ rewardLabel: e.target.value })}
            placeholder={t('rewardField.labelPlaceholder')}
          />
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <label>{t('rewardField.descriptionLabel')}</label>
          <textarea
            rows={2}
            value={reward.rewardDescription ?? ''}
            onChange={(e) => onChange({ rewardDescription: e.target.value })}
          />
        </div>

        <div>
          <label>{t('rewardField.nominalValue')}</label>
          <input
            type="number"
            step={0.01}
            value={reward.rewardValueEur}
            onChange={(e) => onChange({ rewardValueEur: Number(e.target.value) })}
          />
        </div>

        <div>
          <label>{t('rewardField.realCost')}</label>
          <input
            type="number"
            step={0.01}
            value={reward.rewardRealCostEur ?? ''}
            placeholder={t('rewardField.realCostPlaceholder')}
            onChange={(e) =>
              onChange({
                rewardRealCostEur: e.target.value === '' ? null : Number(e.target.value),
              })
            }
          />
        </div>

        <div>
          <label>{t('rewardField.maxWinners')}</label>
          <input
            type="number"
            min={1}
            value={reward.maxWinners ?? ''}
            placeholder={t('rewardField.maxWinnersPlaceholder')}
            onChange={(e) =>
              onChange({
                maxWinners: e.target.value === '' ? null : Number(e.target.value),
              })
            }
          />
          <div className="small muted" style={{ marginTop: '0.25rem' }}>
            {t('rewardField.maxWinnersHint')}
          </div>
        </div>
      </div>
    </div>
  );
}
